import styled from "styled-components";
import { BREAKPOINT_MAX } from "../../styles/responsiveBreakpoints";

/** Wraps Hangar RadioGroup to align spacing with the previous custom radio block */
export const RadioGroupBlock = styled.div<{ $hasError?: boolean }>`
  margin-top: 1rem;
  ${({ $hasError }) =>
    $hasError &&
    `
    padding: 0.25rem;
    border-radius: 0.25rem;
  `}
`;

export const UnifiedCard = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  background: #ffffff;
  border-radius: 0.5rem;
  padding: 1.5rem;

  @media (max-width: ${BREAKPOINT_MAX.xl}px) {
    padding: 1.25rem;
  }

  @media (max-width: ${BREAKPOINT_MAX.md}px) {
    padding: 1rem;
  }
`;

export const TwoColumnSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1px 1fr;
  column-gap: 2rem;
  align-items: stretch;

  @media (max-width: ${BREAKPOINT_MAX.lg}px) {
    grid-template-columns: 1fr;
    column-gap: 0;
    row-gap: 2rem;
  }
`;

/** Separador vertical entre Motivo de cambio y Observaciones (prototipo: 1px #D9D9D9). */
export const ColumnDivider = styled.div`
  width: 1px;
  margin: 0;
  padding: 0;
  background-color: #d9d9d9;
  box-sizing: border-box;

  @media (max-width: ${BREAKPOINT_MAX.lg}px) {
    display: none;
  }
`;

export const ValidationAlertWrapper = styled.div`
  width: 100%;
  margin: 0.3125rem 0 1.5625rem 0;
  display: block;

  & > * {
    width: 100% !important;
    min-width: 100%;
    max-width: 100%;
    box-sizing: border-box;
  }
`;

export const ColumnContent = styled.div`
  display: flex;
  flex-direction: column;
`;

/** Contenedor “Motivo de cambio”; en solo lectura el fondo sigue siendo el de la tarjeta (controles grises a nivel campo). */
export const ChangeReasonPanel = styled.div<{ $readOnly?: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
`;

export const SectionTitleText = styled.div`
  margin-bottom: 1.5rem;
  
  &:first-child {
    margin-top: 0;
  }
`;

export const ChangeCodeSectionTitle = styled.span`
  font-size: 1.5rem;

  & h3,
  & > * {
    font-size: 1.5rem !important;
  }
`;

export const FormGroup = styled.div`
  position: relative;
  margin-bottom: 1.5rem;
`;

export const OutlinedFieldset = styled.fieldset<{
  $readOnly?: boolean;
  $hasError?: boolean;
  /** Kept for call sites; read-only surfaces use Hangar background.secondary. */
  $inMutedPanel?: boolean;
}>`
  position: relative;
  border: 1px solid ${({ theme, $readOnly, $hasError }) => {
    if ($hasError) return '#DC2626';
    if ($readOnly) return theme.tokens.color.border.primary;
    return '#E5E7EB';
  }};
  border-radius: 0.25rem;
  padding: 0.75rem;
  margin: 0;
  background: ${({ theme, $readOnly }) =>
    $readOnly ? theme.tokens.color.background.secondary : theme.tokens.color.background.primary};

  ${({ $readOnly, $hasError }) =>
    !$readOnly &&
    !$hasError &&
    `
    &:focus-within {
      border-color: #1e40af;
    }
  `}
`;

/** Borde compartido: Observaciones, selects condicionales (Nuevo RC / Nuevo remark). */
export const CHANGE_FORM_CONTROL_BORDER = '#5c5c5c';

/** Ancho fijo de controles en detalle de cambio de vuelo (evita que el select crezca con opciones largas). */
export const CHANGE_FORM_CONTROL_WIDTH = "490px";

const FORM_CONTROL_WIDTH_BOX = `
  width: ${CHANGE_FORM_CONTROL_WIDTH};
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
`;

const COMBOBOX_OVERFLOW = `
  & [role='combobox'] {
    width: 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
    overflow: hidden;
  }
  & [role='combobox'] span,
  & [role='combobox'] p {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

/** Contenedor de ancho fijo para bloques alineados (RC inicial, radios, etc.). */
export const FormControlFixedWidth = styled.div`
  ${FORM_CONTROL_WIDTH_BOX}
`;

/** Selects alineados al prototipo: sin recuadro del fieldset; el control Hangar aporta el layout. */
export const BorderlessFieldWrap = styled.div`
  ${FORM_CONTROL_WIDTH_BOX}
  margin: 0;
  padding: 0;
  ${COMBOBOX_OVERFLOW}
`;

/**
 * Solo lectura (dato de referencia, p. ej. RC inicial de Croamis): fondo gris, borde visible,
 * label y valor legibles — prototipo imagen 1.
 */
export const CHANGE_FORM_READONLY_SURFACE = '#E0E0E0';
export const CHANGE_FORM_READONLY_BORDER = '#757575';
export const CHANGE_FORM_READONLY_TEXT = '#424242';
export const CHANGE_FORM_READONLY_LABEL = '#5c5c5c';
export const CHANGE_FORM_READONLY_HELPER = '#757575';
export const CHANGE_FORM_READONLY_RADIUS = '6px';

/**
 * Deshabilitado (caso bloqueado / control no disponible): sin borde, label y valor muy apagados — prototipo imagen 2.
 */
export const CHANGE_FORM_DISABLED_SURFACE = '#E0E0E0';
export const CHANGE_FORM_DISABLED_TEXT = '#BDBDBD';
export const CHANGE_FORM_DISABLED_RADIUS = '8px';

const CHANGE_FORM_RADIO_SELECTED_RING = '#0d12ab';

/**
 * Selects al elegir "No, quiero registrar uno nuevo": label 14px bold, combobox 48px y borde #5c5c5c.
 */
export const ChangeReasonNewFieldsSelectWrap = styled.div`
  ${FORM_CONTROL_WIDTH_BOX}
  ${COMBOBOX_OVERFLOW}

  & label[for] {
    font-size: 14px !important;
    font-weight: 700 !important;
    line-height: 1.25 !important;
  }

  /* Activo: label oscuro. Deshabilitado: gris apagado (no pisar Hangar / prototipo imagen 2). */
  &:has([role='combobox']:not([aria-disabled='true'])) label[for] {
    color: ${CHANGE_FORM_CONTROL_BORDER} !important;
  }

  &:has([role='combobox'][aria-disabled='true']) label[for] {
    color: ${CHANGE_FORM_DISABLED_TEXT} !important;
  }

  & [role='combobox']:where(:not([aria-disabled='true'])) {
    height: 48px !important;
    min-height: 48px !important;
    max-height: 48px !important;
    box-sizing: border-box !important;
    border: 1px solid ${CHANGE_FORM_CONTROL_BORDER} !important;
    border-radius: 0.5rem !important;
    box-shadow: none !important;
  }

  & [role='combobox'][aria-invalid='true']:not([aria-disabled='true']) {
    border-color: #b40e3b !important;
  }

  & [role='combobox']:focus-visible:not([aria-disabled='true']) {
    border-color: #1e40af !important;
  }

  /* Solo lectura: sin borde; color de label y superficie los aporta HangarSelectField ($inactive). */
  & [role='combobox'][aria-disabled='true'] {
    height: 48px !important;
    min-height: 48px !important;
    max-height: 48px !important;
    box-sizing: border-box !important;
    border: none !important;
    border-radius: ${CHANGE_FORM_DISABLED_RADIUS} !important;
    box-shadow: none !important;
  }
`;

/**
 * Radio "¿El reason code de Croamis es correcto?": anillo azul al seleccionar (formulario activo).
 * Caso deshabilitado (imagen 2): la pregunta (legend) sigue legible en gris oscuro; opciones e íconos apagados.
 */
export const ChangeReasonRadioGroupWrap = styled.div<{ $readOnly?: boolean }>`
  ${FORM_CONTROL_WIDTH_BOX}

  & legend {
    font-size: 14px !important;
    font-weight: 700 !important;
    color: ${CHANGE_FORM_CONTROL_BORDER} !important;
    margin-bottom: 0.5rem;
    padding: 0;
  }

  ${({ $readOnly }) =>
    $readOnly &&
    `
    & legend,
    & legend span {
      color: #424242 !important;
      opacity: 1 !important;
    }
    & label[aria-disabled='true'] span {
      color: ${CHANGE_FORM_DISABLED_TEXT} !important;
    }
    /* Radio seleccionado en grupo deshabilitado: relleno gris, sin anillo azul */
    & div:has(> input[type='radio']:checked:disabled) + label [data-testid$='--radio__icon'] {
      border-color: ${CHANGE_FORM_DISABLED_TEXT} !important;
      border-width: 1px !important;
      background-color: ${CHANGE_FORM_DISABLED_TEXT} !important;
    }
    & div:has(> input[type='radio']:checked:disabled) + label [data-testid$='--radio__icon'] svg {
      color: ${CHANGE_FORM_DISABLED_SURFACE} !important;
    }
  `}

  & div:has(> input[type='radio']:checked:not(:disabled)) + label [data-testid$='--radio__icon'] {
    border-color: ${CHANGE_FORM_RADIO_SELECTED_RING} !important;
    border-width: 2px !important;
    box-sizing: border-box;
  }

  & div:has(> input[type='radio']:checked:not(:disabled)) + label [data-testid$='--radio__icon'] svg {
    color: ${CHANGE_FORM_RADIO_SELECTED_RING} !important;
  }

  /* Hangar pone outline al ícono en :focus-within del layout; sin esto se ve un “doble anillo” con nuestro borde. */
  & [data-testid$='--radio__layout']:focus-within [data-testid$='--radio__icon'] {
    outline: none !important;
    outline-offset: 0 !important;
    box-shadow: none !important;
  }

  & input[type='radio']:focus,
  & input[type='radio']:focus-visible {
    outline: none !important;
  }
`;

/**
 * Reason code inicial: con caso editable → solo lectura con borde (referencia Croamis).
 * Con formulario deshabilitado → mismo tratamiento apagado que el resto de campos (imagen 2).
 */
export const InitialReasonCodeLabel = styled.label<{ $formDisabled?: boolean }>`
  display: block;
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 0.625rem;
  font-family: "LATAM Sans Regular", sans-serif;
  color: ${({ $formDisabled }) =>
    $formDisabled ? CHANGE_FORM_DISABLED_TEXT : CHANGE_FORM_READONLY_LABEL};
`;

export const InitialReasonCodeBox = styled.div<{ $formDisabled?: boolean }>`
  display: flex;
  align-items: stretch;
  width: 100%;
  height: 48px;
  min-height: 48px;
  padding: 0 1rem;
  margin: 0;
  box-sizing: border-box;
  border-radius: ${({ $formDisabled }) =>
    $formDisabled ? CHANGE_FORM_DISABLED_RADIUS : CHANGE_FORM_READONLY_RADIUS};
  background: ${CHANGE_FORM_READONLY_SURFACE};
  border: ${({ $formDisabled }) =>
    $formDisabled ? "none" : `1px solid ${CHANGE_FORM_READONLY_BORDER}`};
`;

export const InitialReasonCodeInput = styled.input`
  flex: 1;
  min-width: 0;
  width: 100%;
  border: none;
  outline: none;
  font-size: 0.875rem;
  font-family: "LATAM Sans Regular", sans-serif;
  background: transparent;
  box-sizing: border-box;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:not(:disabled) {
    color: ${CHANGE_FORM_READONLY_TEXT};
    cursor: default;
  }

  &[readonly]:not(:disabled) {
    cursor: text;
    user-select: text;
    white-space: nowrap;
  }

  &:disabled {
    cursor: not-allowed;
    color: ${CHANGE_FORM_DISABLED_TEXT};
    -webkit-text-fill-color: ${CHANGE_FORM_DISABLED_TEXT};
    opacity: 1;
  }
`;

/** Helper bajo el RC inicial: legible en modo referencia; apagado si el caso está bloqueado. */
export const InitialReasonCodeHelperText = styled.span<{ $formDisabled?: boolean }>`
  display: block;
  font-size: 0.75rem;
  margin-top: 0.25rem;
  font-family: "LATAM Sans Regular", sans-serif;
  color: ${({ $formDisabled }) =>
    $formDisabled ? CHANGE_FORM_DISABLED_TEXT : CHANGE_FORM_READONLY_HELPER};
`;

export const OutlinedLegend = styled.legend<{ $hasError?: boolean }>`
  font-size: 0.875rem;
  color: ${({ theme, $hasError }) => $hasError ? '#DC2626' : theme.tokens.color.text.secondary};
  padding: 0 0.25rem;
  margin-left: -0.25rem;
  font-family: "LATAM Sans Regular", sans-serif;
`;

export const FormHelperText = styled.span<{ $readOnly?: boolean }>`
  display: block;
  font-size: 0.75rem;
  color: ${({ theme, $readOnly }) =>
    $readOnly ? CHANGE_FORM_DISABLED_TEXT : theme.tokens.color.text.secondary};
  margin-top: 0.25rem;
  font-family: "LATAM Sans Regular", sans-serif;
`;

export const ErrorText = styled.span`
  display: block;
  font-size: 0.75rem;
  color: #DC2626;
  margin-top: 0.25rem;
  font-family: "LATAM Sans Regular", sans-serif;
`;


export const ContactsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
`;

export const ContactChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 0.75rem;
  background: #E7E8FD;
  border-radius: 1rem;
  font-size: 0.875rem;
  color: #000000;
  font-family: "LATAM Sans Regular", sans-serif;
`;

export const ObservationsOptionalLabel = styled.label<{ $readOnly?: boolean }>`
  display: block;
  font-size: 0.875rem;
  font-weight: 700;
  color: ${({ theme, $readOnly }) =>
    $readOnly ? CHANGE_FORM_DISABLED_TEXT : theme.tokens.color.text.secondary};
  margin-bottom: 0.625rem;
  font-family: "LATAM Sans Regular", sans-serif;
`;

export const ObservationsInput = styled.input`
  width: ${CHANGE_FORM_CONTROL_WIDTH};
  max-width: 100%;
  height: 48px;
  min-height: 48px;
  box-sizing: border-box;
  padding: 0 0.75rem;
  font-size: 0.875rem;
  font-family: "LATAM Sans Regular", sans-serif;
  border: 1px solid ${CHANGE_FORM_CONTROL_BORDER};
  border-radius: 0.5rem;
  outline: none;
  background: ${({ theme }) => theme.tokens.color.background.primary};
  color: ${({ theme }) => theme.tokens.color.text.primary};

  &::placeholder {
    color: ${({ theme }) => theme.tokens.color.text.secondary};
  }

  &:focus:not(:disabled) {
    border-color: #1e40af;
  }

  &:disabled {
    cursor: not-allowed;
    border: none;
    border-radius: ${CHANGE_FORM_DISABLED_RADIUS};
    min-height: 7.5rem;
    padding: 0.75rem 1rem;
    background: ${CHANGE_FORM_DISABLED_SURFACE};
    color: ${CHANGE_FORM_DISABLED_TEXT};
  }
`;

export const ReasonCodeContainer = styled.div`
  margin-top: 1.5rem;
`;

export const Footer = styled.footer`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 1rem;
  row-gap: 0.75rem;
  padding: 1.5rem 0;

  @media (max-width: ${BREAKPOINT_MAX.md}px) {
    flex-direction: column-reverse;
    align-items: stretch;

    & > * {
      width: 100%;
      max-width: none;
      box-sizing: border-box;
    }
  }
`;

